import styled from "styled-components/macro";
import {
  lightAccentInfo70,
  lightAccentSuccess70,
  lightBlueLiveBackground,
  lightGreenBackground,
  lightRedBackground,
} from "../../../utils/tier0-colors";
import SemiCircle from "./SemiCircle";
import DoneIcon from "./DoneIcon";
import { ChartPluginsProps } from "../../types";
import { useMeasure } from "react-use";
import ErrorBoundary from "components/SimpleComponents/Errors/ErrorBoundary";
import { uniqBy } from "lodash";
export function SkuRowColumn({
  dimensions,
  skuCodeChangesHistory,
  isCurrentShift,
  scale,
}: {
  dimensions: {
    height: number;
    width: number;
  };
  skuCodeChangesHistory: ChartPluginsProps["skuCodeChangesHistory"];
  isCurrentShift: ChartPluginsProps["isCurrentShift"];
  scale: number;
}) {
  return (
    <SkuRowColumnStyles {...{ $dimensions: dimensions, $scale: scale }}>
      {/* temp patch-fix to duplicate keys issue */}
      {uniqBy(
        skuCodeChangesHistory,
        item => `${item.skuCode}-${item.ge}-${item.lt}`
      ).map(
        ({ skuCode, skuName, ge, lt, isAboveTarget, target, output }, idx) => {
          const isLast = idx === skuCodeChangesHistory.length - 1;
          const isLive =
            !lt || Number(lt) > Date.now() || (isCurrentShift && isLast);
          const duration = lt - ge;

          return (
            <SkuRowItem
              key={`${skuCode}-${ge}-${lt}`}
              {...{
                skuCodeChangesHistory,
                skuCode,
                ge,
                duration,
                skuName,
                isLive,
                isAboveTarget,
                target,
                output,
                scale,
              }}
            />
          );
        }
      )}
    </SkuRowColumnStyles>
  );
}

function SkuRowItem({
  skuCodeChangesHistory,
  skuCode,
  ge,
  duration,
  skuName,
  isLive,
  isAboveTarget,
  target,
  output,
  scale,
}: {
  skuCodeChangesHistory: ChartPluginsProps["skuCodeChangesHistory"];
  skuCode: string | null;
  ge: number;
  duration: number;
  skuName: string | null;
  isLive: boolean;
  isAboveTarget: boolean;
  target: number;
  output: number;
  scale: number;
}) {
  const [ref, dimensions] = useMeasure<HTMLDivElement>();
  const isTooSmallToFitText = dimensions.height < 50;
  const isTiny = dimensions.height < 13;
  return (
    <div
      ref={ref}
      key={`${skuCode}-${ge}`}
      className={`work-order${
        isTooSmallToFitText ? " too-small-to-fit-text" : ""
      }${isTiny ? " tiny" : ""}`}
      style={{
        flexGrow:
          skuCodeChangesHistory.length === 1 // if it's the only team size, grow to 100%
            ? 1
            : duration,
      }}
    >
      <div className="left">
        <div className="zeroHeightWrapper">
          <div className="text">
            <span>{skuCode}</span> <span>|</span> <span>{skuName}</span>
          </div>
          {/* * leaving this here - handy for debugging target / output per work order */}
          {/* <div className="text" style={{ transform: "scale(0.8)", transformOrigin: "left" }}>
            <span>{Math.round(output)}</span> <span>/</span> <span>{Math.round(target)}</span>
          </div> */}
        </div>
      </div>
      <div
        className={`right ${
          isLive ? "live" : isAboveTarget ? "above-target" : "below-target"
        }`}
      >
        <ErrorBoundary>
          <div className="icon">
            {isLive ? (
              "Live"
            ) : isAboveTarget ? (
              <DoneIcon {...{ scale }} />
            ) : (
              <SemiCircle {...{ scale }} />
            )}
          </div>
        </ErrorBoundary>
      </div>
    </div>
  );
}

export const BORDER_WIDTH = 1;
const SkuRowColumnStyles = styled.div<{
  $dimensions: {
    height: number;
    width: number;
  };
  $scale: number;
}>`
  transform: translateY(-1px);
  height: ${({ $dimensions }) => $dimensions.height - BORDER_WIDTH * 2}px;
  display: flex;
  gap: ${p => p.$scale * 2}px;
  flex-direction: column;
  padding-right: ${p => p.$scale * 1}px;
  .zeroHeightWrapper {
    height: 0;
  }
  .work-order {
    font-size: ${p => p.$scale * 18}px;
    line-height: ${p => p.$scale * 26}px;
    height: 0px;
    display: flex;
    span:first-child {
      font-weight: bold;
    }

    .left {
      flex-grow: 1;
    }
    .right {
      border-radius: ${p => p.$scale * 2}px;
      width: ${p => p.$scale * 32}px;
      flex-shrink: 0;
      margin: ${p => p.$scale * 3}px;
      height: calc(100% - ${p => p.$scale * 6}px);
      display: flex;
      align-items: center;
      justify-content: center;
      &.above-target {
        background: ${lightGreenBackground};
        .MuiSvgIcon-root svg {
          color: ${lightAccentSuccess70};
        }
      }
      &.below-target {
        background: ${lightRedBackground};
      }
      &.live {
        background: ${lightBlueLiveBackground};
        .icon {
          color: ${lightAccentInfo70};
          font-size: ${p => p.$scale * 12}px;
          font-weight: bold;
        }
      }
    }
    .icon {
      display: flex;
    }
    &.too-small-to-fit-text {
      overflow: hidden;
      span {
        line-height: ${p => p.$scale * 1}em;
      }
      .left .zeroHeightWrapper {
        font-size: ${p => p.$scale * 14}px;
        padding-top: 0;
        height: 100%;
        position: relative;
        .text {
          position: absolute;
          top: 0;
          bottom: 0;
          display: flex;
          gap: ${p => p.$scale * 5}px;
          align-items: center;
        }
      }
      .right {
        margin-top: 0;
        margin-bottom: 0;
        height: 100%;
      }
      &.tiny {
        span {
          white-space: nowrap;
        }
        .left .zeroHeightWrapper {
          font-size: ${p => p.$scale * 10}px;
        }
        .icon {
          visibility: hidden;
        }
      }
    }
  }
`;
